if (!customElements.get('product-modal')) {
  customElements.define(
    'product-modal',
    class ProductModal extends ModalDialog {
      constructor() {
        super();
        this._activeSlide = null;
        this._observer = null;
        this._slides = [];
        this._prev = null;
        this._next = null;
        this._slider = null;
      }

      connectedCallback() {
        if (typeof super.connectedCallback === 'function') super.connectedCallback();

        this._slider = this.querySelector('.lightbox-slider');
        this._slides = Array.from(this.querySelectorAll('.lightbox-slider > [data-media-id]'));
        this._prev = this.querySelector('.lightbox-arrow--prev');
        this._next = this.querySelector('.lightbox-arrow--next');

        if (this._prev) this._prev.addEventListener('click', () => this._advance(-1));
        if (this._next) this._next.addEventListener('click', () => this._advance(1));
        this.addEventListener('keydown', this._onKeydown);
        document.addEventListener('fullscreenchange', this._onFullscreenChange);

        if (this._slider) {
          this._observer = new IntersectionObserver(
            (entries) => this._onIntersect(entries),
            { root: this._slider, threshold: 0.6 }
          );
          this._slides.forEach((s) => this._observer.observe(s));
        }
      }

      disconnectedCallback() {
        if (this._observer) this._observer.disconnect();
        document.removeEventListener('fullscreenchange', this._onFullscreenChange);
      }

      show(opener) {
        super.show(opener);
        this.showActiveMedia();
      }

      hide() {
        const activeMediaId = this._activeSlide ? this._activeSlide.dataset.mediaId : null;
        this._pauseAllVideos();
        super.hide();
        if (activeMediaId) {
          document.dispatchEvent(
            new CustomEvent('lightboxClosed', { detail: { mediaId: activeMediaId }, bubbles: true })
          );
        }
      }

      showActiveMedia() {
        if (!this.openedBy) return;
        const openingMediaId = this.openedBy.getAttribute('data-media-id');
        const target =
          this._slides.find((s) => s.dataset.mediaId === openingMediaId) || this._visibleSlides()[0];
        if (!target) return;

        // Land instantly on the requested slide — smooth scrolling across
        // several slides on open feels jarring. Defer one frame so the
        // modal layout has settled (avoids the scrollIntoView no-op when
        // the container is still 0×0 at show() time).
        requestAnimationFrame(() => {
          target.scrollIntoView({ block: 'nearest', inline: 'center', behavior: 'auto' });
          this._activeSlide = target;
          this._updateAriaAndArrows();

          // Preserve original Dawn behaviour for deferred YouTube media.
          const deferredTpl = target.querySelector && target.querySelector('template');
          const content = deferredTpl ? deferredTpl.content : null;
          if (target.nodeName === 'DEFERRED-MEDIA' && content && content.querySelector('.js-youtube')) {
            target.loadContent && target.loadContent();
          }
        });
      }

      _visibleSlides() {
        return this._slides.filter((s) => {
          if (s.hasAttribute('hidden') || s.classList.contains('is-hidden')) return false;
          const cs = window.getComputedStyle(s);
          return cs.display !== 'none';
        });
      }

      _advance(direction) {
        const visible = this._visibleSlides();
        const idx = visible.indexOf(this._activeSlide);
        const next = visible[Math.max(0, Math.min(visible.length - 1, idx + direction))];
        if (!next || next === this._activeSlide) return;
        next.scrollIntoView({ block: 'nearest', inline: 'center', behavior: 'smooth' });
      }

      _onKeydown = (e) => {
        if (e.key === 'ArrowLeft') {
          e.preventDefault();
          this._advance(-1);
        } else if (e.key === 'ArrowRight') {
          e.preventDefault();
          this._advance(1);
        }
        // Escape is handled by ModalDialog base.
      };

      _onIntersect(entries) {
        // Pick the most-intersecting visible slide as the active one.
        const intersecting = entries.filter((e) => e.isIntersecting && e.intersectionRatio >= 0.6);
        if (!intersecting.length) return;
        intersecting.sort((a, b) => b.intersectionRatio - a.intersectionRatio);
        const active = intersecting[0].target;
        if (active === this._activeSlide) return;

        // Pause any video that's no longer the active slide.
        entries.forEach((e) => {
          if (!e.isIntersecting || e.intersectionRatio < 0.3) {
            const v = e.target.querySelector && e.target.querySelector('video');
            if (v && !v.paused) {
              v.pause();
              v.currentTime = 0;
            }
          }
        });

        this._activeSlide = active;
        this._updateAriaAndArrows();
      }

      _updateAriaAndArrows() {
        this._slides.forEach((s) => s.removeAttribute('aria-current'));
        if (this._activeSlide) this._activeSlide.setAttribute('aria-current', 'true');

        const visible = this._visibleSlides();
        const idx = visible.indexOf(this._activeSlide);
        if (this._prev) this._prev.toggleAttribute('hidden', idx <= 0);
        if (this._next) this._next.toggleAttribute('hidden', idx >= visible.length - 1);
      }

      _pauseAllVideos() {
        this.querySelectorAll('video').forEach((v) => {
          if (!v.paused) v.pause();
          v.currentTime = 0;
        });
      }

      _onFullscreenChange = () => {
        if (!document.fullscreenElement) this._pauseAllVideos();
      };
    }
  );
}
